import {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import AdmicityTableV2 from '../shared-components/Table/V2/AdmicityTableV2';
import {formatDate} from '../utility/dateUtil';
import FILTER_OPERATIONS from '../constants/filterOperations';
import useNotification from '../utility/hooks/useNotification';
import {useLocation, useNavigate} from 'react-router-dom';
import {useGetApprovalsByApprovalPersonIdQuery, useSubmitApprovalMutation} from '../api/services/formApprovalsService';
import {Stack, styled, TextField} from '@mui/material';
import AdmicityStatus from '../shared-components/AdmicityStatus'
import AdmicityDialog from './AdmicityDialog';
import {APPROVAL_STATUSES, getApprovalStatusInfo} from '../constants/FormApprovalStatuses';
import {FORM_STATUSES} from '../constants/formStatuses';
import {FORM_REQUEST_STATUSES} from '../constants/formRequestStatuses';
import DateRangePicker from '../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import Tooltip from '@mui/material/Tooltip';

const StyledCell = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '24px',
});

const CustomCell = ({cell}) => {
    const value = cell.getValue();
    return (
        <Tooltip title={value} arrow>
            <StyledCell>
                {value}
            </StyledCell>
        </Tooltip>
    );
};
CustomCell.propTypes = {
    cell: PropTypes.object.isRequired
};

const ApprovalFormsTable = ({employeeId}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        showInfoNotification,
        showErrorNotification
    } = useNotification();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [note, setNote] = useState('');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    const {
        data,
        isLoading,
        isFetching
    } = useGetApprovalsByApprovalPersonIdQuery({
        employeeId,
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
    }, {skip: !employeeId});
    const approvals = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;
    const [submitApproval] = useSubmitApprovalMutation();
    const handleAction = async (status) => {
        if (!selectedAssignment?.request) return;
        const result = await submitApproval({
            formId: selectedAssignment.stage.formId,
            assignmentId: selectedAssignment.id,
            status,
            note,
            employeeId
        });

        if (result.error) {
            showErrorNotification(`Failed to ${status.toLowerCase()} form.`);
        } else {
            showInfoNotification(`Form ${status.toLowerCase()} successfully.`);
            setDialogOpen(false);
            setNote('');
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'stage.name',
            header: 'Approval Stage',
            filterFn: FILTER_OPERATIONS.CONTAINS,
        },
        {
            id: 'stageProgress',
            header: 'Approval Progress',
            accessorFn: row => `Stage ${row.stageNumber} / ${row.stageCount}`,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'status',
            header: 'Approval Status',
            // eslint-disable-next-line react/prop-types
            Cell: ({cell: {getValue}}) => (
                <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
            ),
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: Object.values(APPROVAL_STATUSES).map(s => ({
                value: s,
                label: s
            }))
        },
        {
            accessorKey: 'request.formName',
            header: 'Form Name',
            filterFn: FILTER_OPERATIONS.CONTAINS,
        },
        {
            accessorKey: 'request.editedBy.fullname',
            header: 'Submitted By',
            filterFn: FILTER_OPERATIONS.CONTAINS,
        },
        {
            accessorKey: 'request.editedAt',
            header: 'Submitted At',
            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
        },
        {
            accessorKey: 'approvedBy',
            header: 'Approved By',
            filterFn: FILTER_OPERATIONS.CONTAINS,
        },
        {
            accessorKey: 'approvedAt',
            header: 'Approved At',
            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
        },
        {
            accessorKey: 'approvalNote',
            header: 'Approval Note',
            filterFn: FILTER_OPERATIONS.CONTAINS,
            Cell: CustomCell,
        },
    ], []);

    const historyColumns = useMemo(() => [
        {
            accessorKey: 'stage.name',
            header: 'Stage Name',
        },
        {
            id: 'stageProgress',
            header: 'Progress',
            accessorFn: row => `Stage ${row.stageNumber} / ${row.stageCount}`,
        },
        {
            accessorKey: 'status',
            header: 'Status',
            // eslint-disable-next-line react/prop-types
            Cell: ({cell: {getValue}}) => (
                <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
            )
        },
        {
            accessorKey: 'approvedBy',
            header: 'Approved By',
        },
        {
            accessorKey: 'approvedAt',
            header: 'Approved At',
            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        },
        {
            accessorKey: 'approvalNote',
            header: 'Note',
            Cell: CustomCell
        }
    ], []);

    const rowActions = (row) => {
        const isFormActive = row.request?.form?.status === FORM_STATUSES.active;
        const isSubmitted = row.request?.status === FORM_REQUEST_STATUSES.submitted;

        return [
            {
                label: 'Approve',
                disabled: () =>
                    !isFormActive || !isSubmitted || row.status === 'Approved',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Approved');
                    setDialogOpen(true);
                }
            },
            {
                label: 'Reject',
                disabled: () =>
                    !isFormActive || !isSubmitted ||
                    row.status === 'Approved' || row.status === 'Rejected',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Rejected');
                    setDialogOpen(true);
                }
            },
            {
                label: 'History',
                disabled: () => !row.previousStages?.length,
                action: () => {
                    setHistoryRows(row.previousStages ?? []);
                    setHistoryDialogOpen(true);
                }
            }
        ];
    };

    const handleRowClick = ({ stage, request }) => {
        if (location.pathname.includes('/profiles/employees/')) {
            navigate(`/profiles/employees/${employeeId}/forms/${request.id}/approval`);
        } else {
            navigate(`/request-and-responses/approval/${stage.formId}/${request.id}`);
        }
    };

    return (
        <>
            <AdmicityTableV2
                columns={columns}
                data={approvals}
                totalItemCount={totalCount}
                pagination={pagination}
                isLoading={isLoading}
                isRefetching={isFetching}
                onPaginationModelChange={setPagination}
                rowActions={rowActions}
                onRowClick={handleRowClick}
                noRowsOverlay={{
                    icon: null,
                    text: 'No approvals pending.'
                }}
                tableProps={{
                    enableSorting: false,
                    enableFilters: false
                }}
            />
            <AdmicityDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                title={selectedAction === 'Approved' ? 'Approval Note' : 'Rejection Note'}
                maxWidth="sm"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: () => setDialogOpen(false)
                    },
                    {
                        label: selectedAction,
                        onClick: () => handleAction(selectedAction),
                        disabled: false,
                    }
                ]}
            >
                <Stack spacing={2} mt={1}>
                    <TextField
                        multiline
                        fullWidth
                        minRows={3}
                        label="Optional Note"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                </Stack>
            </AdmicityDialog>
            <AdmicityDialog
                open={historyDialogOpen}
                handleClose={() => setHistoryDialogOpen(false)}
                title="Approval History"
                maxWidth="md"
                actions={[
                    {
                        label: 'Close',
                        onClick: () => setHistoryDialogOpen(false)
                    }
                ]}
            >
                <AdmicityTableV2
                    columns={historyColumns}
                    data={historyRows}
                    totalItemCount={historyRows.length}
                    pagination={{pageIndex: 0, pageSize: historyRows.length}}
                    isLoading={false}
                    isRefetching={false}
                    onPaginationModelChange={() => {
                    }}
                    tableProps={{
                        enableSorting: false,
                        enableFilters: false,
                        enablePagination: false,
                    }}
                />
            </AdmicityDialog>

        </>
    );
};

ApprovalFormsTable.propTypes = {
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired
};

export default ApprovalFormsTable;
